import React from 'react';
import { Layers, Table, TrendingUp, Filter, Palette } from 'lucide-react';
import { DropShelf } from './DropShelf';
import { getShelvesForVizType } from './shelfConfigByVizType';
import { emitCoachEvent } from '@/coach/coachApi';

/**
 * Render the four drop-shelves (row / col / val / filter) appropriate for
 * the active visual's viz type. The configuration is looked up via
 * `getShelvesForVizType(vizType)` which returns an ordered list of
 * `['row' | 'col' | 'val' | 'filter', label]` tuples.
 *
 * All state lives in the parent builder component; this is a pure-render
 * layer so the builder can stay focused on orchestration.
 */
export default function ShelfBuilder({
  vizType,
  rowDims, colDim, valueMeasures, filterDims,
  colorMeasure = '', setColorMeasure,
  allFields, fieldOverrides, dateParts,
  setRowDims, setColDim, setValueMeasures, setFilterDims,
  setDateParts, setSlicerSelections,
  removeFromAllShelves,
  pinFieldEntity,
  onFieldProperties, onPillRightClick,
  onRowDimAdded,
  guessedFields = [],
}) {
  const shelfProps = {
    allFields, fieldOverrides, onFieldProperties, onPillRightClick, guessedFields,
  };

  const addDimToShelf = (fieldName, currentItems, setter, entityId, insertIndex) => {
    const insertAt = (arr, name) => {
      if (insertIndex == null || insertIndex < 0 || insertIndex > arr.length) return [...arr, name];
      const c = [...arr]; c.splice(insertIndex, 0, name); return c;
    };
    const fieldMeta = allFields.find(f => f.name === fieldName);
    const isDate = fieldMeta && (
      fieldMeta.data_type === 'date' ||
      fieldMeta.data_type === 'datetime' ||
      fieldMeta.name.toLowerCase().includes('date')
    );
    if (entityId && pinFieldEntity) pinFieldEntity(fieldName, entityId);
    // If the field already exists and it's a date, create a virtual copy
    // so the user can pin a second date part (e.g. year on one shelf, month on another).
    if (currentItems.includes(fieldName) && isDate) {
      const existing = currentItems.filter(n => n === fieldName || n.startsWith(fieldName + '__'));
      const suffix = existing.length;
      const virtualName = fieldName + '__' + suffix;
      if (!allFields.find(f => f.name === virtualName)) {
        allFields.push({ ...fieldMeta, name: virtualName, _sourceField: fieldName });
      }
      if (entityId && pinFieldEntity) pinFieldEntity(virtualName, entityId);
      setter(p => insertAt(p, virtualName));
      setTimeout(() => {
        setDateParts(prev => ({ ...prev, [virtualName]: null }));
      }, 100);
      return;
    }
    setter(p => p.includes(fieldName) ? p : insertAt(p, fieldName));
    if (onRowDimAdded) onRowDimAdded(fieldName);
    emitCoachEvent('dimension_added', 'report-dimension-shelf');
  };

  const insertInto = (arr, name, insertIndex) => {
    if (arr.includes(name)) return arr;
    if (insertIndex == null || insertIndex < 0 || insertIndex > arr.length) return [...arr, name];
    const c = [...arr]; c.splice(insertIndex, 0, name); return c;
  };

  const shelves = getShelvesForVizType(vizType || 'table');

  return (
    <React.Fragment>
      {shelves.map(([kind, label]) => {
        if (kind === 'row') {
          return (
            <DropShelf
              key="row" label={label} icon={Layers} items={rowDims} acceptType="dimension"
              requireCategory={vizType === 'geo_map' ? 'geographic' : undefined}
              coachTarget="report-dimension-shelf"
              {...shelfProps}
              onDrop={(n, t, eid, idx) => addDimToShelf(n, rowDims, setRowDims, eid, idx)}
              onRemove={n => {
                setRowDims(p => p.filter(x => x !== n));
                setDateParts(prev => { const c = { ...prev }; delete c[n]; return c; });
              }}
              onReorder={setRowDims}
            />
          );
        }
        if (kind === 'col') {
          return (
            <DropShelf
              key="col" label={label} icon={Table} items={colDim ? [colDim] : []} acceptType="dimension"
              {...shelfProps}
              onDrop={(n, t, eid) => { setColDim(n); if (eid && pinFieldEntity) pinFieldEntity(n, eid); }}
              onRemove={() => setColDim('')}
            />
          );
        }
        if (kind === 'val') {
          return (
            <DropShelf
              key="val" label={label} icon={TrendingUp} items={valueMeasures.filter(m => m !== colorMeasure)} acceptType="measure"
              coachTarget="report-measure-shelf"
              {...shelfProps}
              onDrop={(n, t, eid, idx) => { removeFromAllShelves(n); setValueMeasures(p => insertInto(p, n, idx)); emitCoachEvent('measure_added', 'report-measure-shelf'); }}
              onRemove={n => setValueMeasures(p => p.filter(x => x !== n))}
              onReorder={setValueMeasures}
            />
          );
        }
        if (kind === 'colorval') {
          // A measure that drives the word-cloud color gradient. It's stored on
          // the per-visual config AND kept in valueMeasures so the query fetches
          // its values; it's hidden from the Size shelf above to avoid duplication.
          return (
            <DropShelf
              key="colorval" label={label} icon={Palette} items={colorMeasure ? [colorMeasure] : []} acceptType="measure"
              {...shelfProps}
              onDrop={(n) => { if (setColorMeasure) setColorMeasure(n); }}
              onRemove={() => { if (setColorMeasure) setColorMeasure(''); }}
            />
          );
        }
        if (kind === 'filter') {
          const _slicerPickerFields = (allFields || []).filter(f => f && f.semantic_type !== 'measure' && !f._sourceField && !filterDims.includes(f.name));
          return (
            <DropShelf
              key="filter" label="Slicers" icon={Filter} items={filterDims} acceptType="dimension"
              pickerFields={_slicerPickerFields}
              {...shelfProps}
              onDrop={(n, t, eid, idx) => { setFilterDims(p => insertInto(p, n, idx)); if (eid && pinFieldEntity) pinFieldEntity(n, eid); }}
              onRemove={n => {
                setFilterDims(p => p.filter(x => x !== n));
                setSlicerSelections(p => { const c = { ...p }; delete c[n]; return c; });
              }}
              onReorder={setFilterDims}
            />
          );
        }
        return null;
      })}
    </React.Fragment>
  );
}
